import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Printer, Calendar, User, Stethoscope, Eye, FileImage, ClipboardList, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const VisitView = () => {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedImage, setExpandedImage] = useState(null);

  useEffect(() => {
    loadVisit();
  }, [visitId]);

  const loadVisit = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await fetch(`${API}/api/patient/visit/${visitId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVisit(data.visit);
        setPatient(data.patient);
      } else {
        toast.error('Visita non trovata');
        navigate('/dashboard/patient');
      }
    } catch (error) {
      console.error('Error loading visit:', error);
      toast.error('Errore nel caricamento della visita');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    const token = localStorage.getItem('token');
    // Use fetch to download and trigger download dialog
    fetch(`${API}/api/patient/visit/${visitId}/pdf?token=${token}`)
      .then(response => {
        if (!response.ok) throw new Error('Errore nel download');
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cartella_clinica_${visitId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('PDF scaricato con successo');
      })
      .catch(err => {
        console.error('Download error:', err);
        toast.error('Errore nel download del PDF');
      });
  };

  const handlePrint = () => {
    toast.info('Apertura finestra di stampa...');
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Check if there are any instrumental exam images
  const hasInstrumentalExams = () => {
    const examFields = [
      'oct_macula_url_od', 'oct_macula_url_os',
      'oct_nervo_ottico_url_od', 'oct_nervo_ottico_url_os',
      'retinografia_url_od', 'retinografia_url_os',
      'topografia_url_od', 'topografia_url_os',
      'campo_visivo_url_od', 'campo_visivo_url_os',
      'oct_url_od', 'oct_url_os'
    ];
    return examFields.some(field => visit?.[field]);
  };

  // Check if there are attached files
  const hasAttachments = () => {
    return visit?.attachments && visit.attachments.length > 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9FAFB' }}>
        <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full" style={{ borderColor: '#2A9FCB' }}></div>
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9FAFB' }}>
        <p>Visita non trovata</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F9FAFB' }}>
      {/* Header - Non stampabile */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard/patient')}
            className="flex items-center gap-2 hover:opacity-70 transition-opacity"
            style={{ color: '#2A9FCB' }}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Torna alla Dashboard</span>
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="px-4 py-2 rounded-lg flex items-center gap-2 border transition-all hover:bg-gray-50"
              style={{ borderColor: '#2A9FCB', color: '#2A9FCB' }}
              data-testid="print-btn"
            >
              <Printer className="w-4 h-4" />
              Stampa
            </button>
            <button
              onClick={handleDownloadPDF}
              className="px-4 py-2 rounded-lg flex items-center gap-2 text-white transition-all hover:opacity-90"
              style={{ background: '#2A9FCB' }}
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4" />
              Scarica PDF
            </button>
          </div>
        </div>
      </div>

      {/* Contenuto Visita - Stile PDF */}
      <div className="max-w-4xl mx-auto p-6 print:p-0 print:max-w-none">
        <div className="bg-white rounded-xl shadow-lg print:shadow-none print:rounded-none overflow-hidden">
          
          {/* Intestazione Clinica */}
          <div className="p-6 border-b" style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}>
            <div className="flex justify-between items-start text-white">
              <div>
                <img src="/logo-primevision-full.png" alt="PRIME VISION" className="h-8 w-auto object-contain brightness-0 invert mb-1" />
                <p className="text-sm opacity-80">Centro di Eccellenza Oftalmologica</p>
                <p className="text-sm opacity-80">Via Santa Sofia 10, Roma</p>
              </div>
              <div className="text-right text-sm">
                <p className="font-semibold">Referto Visita Oculistica</p>
                <p className="opacity-80">{formatDate(visit.concluded_at)}</p>
              </div>
            </div>
          </div>

          {/* Dati Paziente */}
          <div className="p-6 border-b" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5" style={{ color: '#2A9FCB' }} />
              <h2 className="text-lg font-semibold" style={{ color: '#2A9FCB' }}>Dati Paziente</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Nome</p>
                <p className="font-medium">{patient?.first_name} {patient?.last_name}</p>
              </div>
              <div>
                <p className="text-gray-500">Data di Nascita</p>
                <p className="font-medium">{patient?.date_of_birth || 'N/D'}</p>
              </div>
              <div>
                <p className="text-gray-500">Codice Fiscale</p>
                <p className="font-medium">{patient?.codice_fiscale || 'N/D'}</p>
              </div>
              <div>
                <p className="text-gray-500">Telefono</p>
                <p className="font-medium">{patient?.phone || 'N/D'}</p>
              </div>
            </div>
          </div>

          {/* Dati Medico */}
          <div className="p-6 border-b">
            <div className="flex items-center gap-2 mb-4">
              <Stethoscope className="w-5 h-5" style={{ color: '#2A9FCB' }} />
              <h2 className="text-lg font-semibold" style={{ color: '#2A9FCB' }}>Medico Responsabile</h2>
            </div>
            <p className="font-medium">{visit.doctor_name || 'Dott. PRIME VISION'}</p>
            <p className="text-sm text-gray-500">Specialista in Oftalmologia</p>
          </div>

          {/* Diagnosi, Terapia, Consigli */}
          <div className="p-6 border-b">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList className="w-5 h-5" style={{ color: '#2A9FCB' }} />
              <h2 className="text-lg font-semibold" style={{ color: '#2A9FCB' }}>Esito Visita</h2>
            </div>
            <div className="space-y-4">
              <div className="p-4 rounded-lg" style={{ background: '#F0FAF5' }}>
                <h3 className="font-semibold text-sm mb-2" style={{ color: '#2A9FCB' }}>Diagnosi</h3>
                <p className="whitespace-pre-wrap">{visit.diagnosi || 'Non specificata'}</p>
              </div>
              <div className="p-4 rounded-lg" style={{ background: '#FFF8E7' }}>
                <h3 className="font-semibold text-sm mb-2" style={{ color: '#D97706' }}>Terapia Prescritta</h3>
                <p className="whitespace-pre-wrap">{visit.terapia || 'Nessuna terapia prescritta'}</p>
              </div>
              {visit.consigli && (
                <div className="p-4 rounded-lg" style={{ background: '#EFF6FF' }}>
                  <h3 className="font-semibold text-sm mb-2" style={{ color: '#2563EB' }}>Consigli</h3>
                  <p className="whitespace-pre-wrap">{visit.consigli}</p>
                </div>
              )}
            </div>
          </div>

          {/* Tags Diagnosi */}
          {visit.tags_added && visit.tags_added.length > 0 && (
            <div className="p-6 border-b">
              <h3 className="font-semibold text-sm mb-3" style={{ color: '#2A9FCB' }}>Tags Diagnosi</h3>
              <div className="flex flex-wrap gap-2">
                {visit.tags_added.map((tag, i) => (
                  <span key={i} className="px-3 py-1 rounded-full text-sm font-medium text-white" style={{ background: '#2A9FCB' }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Esame Obiettivo */}
          <div className="p-6 border-b">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5" style={{ color: '#2A9FCB' }} />
              <h2 className="text-lg font-semibold" style={{ color: '#2A9FCB' }}>Esame Obiettivo</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Occhio Destro */}
              <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#2A9FCB' }}>
                <div className="px-4 py-2 text-center font-bold text-white" style={{ background: '#2A9FCB' }}>
                  👁️ OCCHIO DESTRO (OD)
                </div>
                <div className="p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: '#2A9FCB', fontWeight: 600 }}>Visus</span>
                    <span className="font-medium">{visit.visus_od || 'N/D'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#2A9FCB', fontWeight: 600 }}>Tonometria</span>
                    <span className="font-medium">{visit.tono_od || 'N/D'} mmHg</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#2A9FCB', fontWeight: 600 }}>Annessi</span>
                    <span className="font-medium">{visit.annessi_od || 'Sani'}</span>
                  </div>
                </div>
              </div>

              {/* Occhio Sinistro */}
              <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#F59E0B' }}>
                <div className="px-4 py-2 text-center font-bold text-white" style={{ background: '#F59E0B' }}>
                  👁️ OCCHIO SINISTRO (OS)
                </div>
                <div className="p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: '#2A9FCB', fontWeight: 600 }}>Visus</span>
                    <span className="font-medium">{visit.visus_os || 'N/D'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#2A9FCB', fontWeight: 600 }}>Tonometria</span>
                    <span className="font-medium">{visit.tono_os || 'N/D'} mmHg</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#2A9FCB', fontWeight: 600 }}>Annessi</span>
                    <span className="font-medium">{visit.annessi_os || 'Sani'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Segmento Anteriore */}
          {(visit.segmento_anteriore_od || visit.segmento_anteriore_os) && (
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#2A9FCB' }}>Segmento Anteriore</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
                  <h3 className="font-semibold text-sm mb-2" style={{ color: '#2A9FCB' }}>Occhio Destro (OD)</h3>
                  <p className="text-sm">{visit.segmento_anteriore_od || 'Nella norma'}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ background: '#FFFBEB' }}>
                  <h3 className="font-semibold text-sm mb-2" style={{ color: '#F59E0B' }}>Occhio Sinistro (OS)</h3>
                  <p className="text-sm">{visit.segmento_anteriore_os || 'Nella norma'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Fondo Oculare */}
          {(visit.fondo_oculare_od || visit.fondo_oculare_os || visit.referto_retinografia_od || visit.referto_retinografia_os) && (
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#2A9FCB' }}>Fondo Oculare</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
                  <h3 className="font-semibold text-sm mb-2" style={{ color: '#2A9FCB' }}>Occhio Destro (OD)</h3>
                  <p className="text-sm whitespace-pre-wrap">{visit.fondo_oculare_od || visit.referto_retinografia_od || 'Nella norma'}</p>
                </div>
                <div className="p-4 rounded-lg" style={{ background: '#FFFBEB' }}>
                  <h3 className="font-semibold text-sm mb-2" style={{ color: '#F59E0B' }}>Occhio Sinistro (OS)</h3>
                  <p className="text-sm whitespace-pre-wrap">{visit.fondo_oculare_os || visit.referto_retinografia_os || 'Nella norma'}</p>
                </div>
              </div>
            </div>
          )}

          {/* OCT - Referti */}
          {(visit.referto_oct_od || visit.referto_oct_os) && (
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#2A9FCB' }}>Referto OCT</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {visit.referto_oct_od && (
                  <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
                    <h3 className="font-semibold text-sm mb-2" style={{ color: '#2A9FCB' }}>Occhio Destro (OD)</h3>
                    <p className="text-sm whitespace-pre-wrap">{visit.referto_oct_od}</p>
                  </div>
                )}
                {visit.referto_oct_os && (
                  <div className="p-4 rounded-lg" style={{ background: '#FFFBEB' }}>
                    <h3 className="font-semibold text-sm mb-2" style={{ color: '#F59E0B' }}>Occhio Sinistro (OS)</h3>
                    <p className="text-sm whitespace-pre-wrap">{visit.referto_oct_os}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Campo Visivo - Referti */}
          {(visit.referto_campo_visivo_od || visit.referto_campo_visivo_os) && (
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#2A9FCB' }}>Referto Campo Visivo</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {visit.referto_campo_visivo_od && (
                  <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
                    <h3 className="font-semibold text-sm mb-2" style={{ color: '#2A9FCB' }}>Occhio Destro (OD)</h3>
                    <p className="text-sm whitespace-pre-wrap">{visit.referto_campo_visivo_od}</p>
                  </div>
                )}
                {visit.referto_campo_visivo_os && (
                  <div className="p-4 rounded-lg" style={{ background: '#FFFBEB' }}>
                    <h3 className="font-semibold text-sm mb-2" style={{ color: '#F59E0B' }}>Occhio Sinistro (OS)</h3>
                    <p className="text-sm whitespace-pre-wrap">{visit.referto_campo_visivo_os}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Esami Strumentali - Immagini */}
          {hasInstrumentalExams() && (
            <div className="p-6 border-b">
              <div className="flex items-center gap-2 mb-4">
                <FileImage className="w-5 h-5" style={{ color: '#2A9FCB' }} />
                <h2 className="text-lg font-semibold" style={{ color: '#2A9FCB' }}>Esami Strumentali - Immagini</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* OCT Macula */}
                {visit.oct_macula_url_od && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#2A9FCB' }}>OCT Macula OD</p>
                    <img 
                      src={visit.oct_macula_url_od} 
                      alt="OCT Macula OD" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.oct_macula_url_od)}
                    />
                  </div>
                )}
                {visit.oct_macula_url_os && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#F59E0B' }}>OCT Macula OS</p>
                    <img 
                      src={visit.oct_macula_url_os} 
                      alt="OCT Macula OS" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.oct_macula_url_os)}
                    />
                  </div>
                )}
                
                {/* OCT Nervo Ottico */}
                {visit.oct_nervo_ottico_url_od && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#2A9FCB' }}>OCT Nervo Ottico OD</p>
                    <img 
                      src={visit.oct_nervo_ottico_url_od} 
                      alt="OCT Nervo Ottico OD" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.oct_nervo_ottico_url_od)}
                    />
                  </div>
                )}
                {visit.oct_nervo_ottico_url_os && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#F59E0B' }}>OCT Nervo Ottico OS</p>
                    <img 
                      src={visit.oct_nervo_ottico_url_os} 
                      alt="OCT Nervo Ottico OS" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.oct_nervo_ottico_url_os)}
                    />
                  </div>
                )}

                {/* Legacy OCT fields */}
                {visit.oct_url_od && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#2A9FCB' }}>OCT OD</p>
                    <img 
                      src={visit.oct_url_od} 
                      alt="OCT OD" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.oct_url_od)}
                    />
                  </div>
                )}
                {visit.oct_url_os && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#F59E0B' }}>OCT OS</p>
                    <img 
                      src={visit.oct_url_os} 
                      alt="OCT OS" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.oct_url_os)}
                    />
                  </div>
                )}
                
                {/* Retinografia */}
                {visit.retinografia_url_od && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#2A9FCB' }}>Retinografia OD</p>
                    <img 
                      src={visit.retinografia_url_od} 
                      alt="Retinografia OD" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.retinografia_url_od)}
                    />
                  </div>
                )}
                {visit.retinografia_url_os && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#F59E0B' }}>Retinografia OS</p>
                    <img 
                      src={visit.retinografia_url_os} 
                      alt="Retinografia OS" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.retinografia_url_os)}
                    />
                  </div>
                )}
                
                {/* Topografia */}
                {visit.topografia_url_od && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#2A9FCB' }}>Topografia OD</p>
                    <img 
                      src={visit.topografia_url_od} 
                      alt="Topografia OD" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.topografia_url_od)}
                    />
                  </div>
                )}
                {visit.topografia_url_os && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#F59E0B' }}>Topografia OS</p>
                    <img 
                      src={visit.topografia_url_os} 
                      alt="Topografia OS" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.topografia_url_os)}
                    />
                  </div>
                )}
                
                {/* Campo Visivo */}
                {visit.campo_visivo_url_od && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#2A9FCB' }}>Campo Visivo OD</p>
                    <img 
                      src={visit.campo_visivo_url_od} 
                      alt="Campo Visivo OD" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.campo_visivo_url_od)}
                    />
                  </div>
                )}
                {visit.campo_visivo_url_os && (
                  <div className="text-center">
                    <p className="text-sm font-medium mb-2" style={{ color: '#F59E0B' }}>Campo Visivo OS</p>
                    <img 
                      src={visit.campo_visivo_url_os} 
                      alt="Campo Visivo OS" 
                      className="rounded-lg max-h-48 mx-auto cursor-pointer hover:opacity-80 transition-opacity border"
                      onClick={() => setExpandedImage(visit.campo_visivo_url_os)}
                    />
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-4 text-center">Clicca su un&apos;immagine per ingrandirla</p>
            </div>
          )}

          {/* Allegati */}
          {hasAttachments() && (
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#2A9FCB' }}>Documenti Allegati</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {visit.attachments.map((attachment, idx) => (
                  <a 
                    key={idx}
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                    style={{ borderColor: '#E5E7EB' }}
                  >
                    <FileImage className="w-8 h-8" style={{ color: '#2A9FCB' }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" style={{ color: '#2A9FCB' }}>{attachment.name || `Allegato ${idx + 1}`}</p>
                      <p className="text-xs text-gray-500">{attachment.type || 'Documento'}</p>
                    </div>
                    <Download className="w-5 h-5 text-gray-400" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Prossimo Controllo */}
          {visit.prossimo_controllo && (
            <div className="p-6 border-b">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-5 h-5" style={{ color: '#2A9FCB' }} />
                <h2 className="text-lg font-semibold" style={{ color: '#2A9FCB' }}>Prossimo Controllo</h2>
              </div>
              <div className="p-4 rounded-lg flex items-center gap-3" style={{ background: '#FEF3C7' }}>
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <p className="font-medium text-amber-800">{visit.prossimo_controllo}</p>
              </div>
            </div>
          )}

          {/* Note Aggiuntive */}
          {visit.note && (
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold mb-3" style={{ color: '#2A9FCB' }}>Note Aggiuntive</h2>
              <p className="text-sm whitespace-pre-wrap text-gray-700">{visit.note}</p>
            </div>
          )}

          {/* Footer */}
          <div className="p-6 text-center text-xs" style={{ background: '#F9FAFB', color: '#9CA3AF' }}>
            <p className="font-semibold" style={{ color: '#2A9FCB' }}>PRIME VISION - Centro di Eccellenza Oftalmologica</p>
            <p>Via Santa Sofia 10, Roma | Tel: 02 1234567 | info@primevision.it</p>
            <p className="mt-2">Documento generato il {new Date().toLocaleDateString('it-IT')} - Questo documento è strettamente confidenziale</p>
          </div>
        </div>
      </div>

      {/* Modal Immagine Ingrandita */}
      {expandedImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-pointer print:hidden"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img 
              src={expandedImage} 
              alt="Immagine ingrandita" 
              className="max-w-full max-h-[90vh] rounded-lg"
            />
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
            >
              <span className="text-2xl leading-none">&times;</span>
            </button>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:rounded-none { border-radius: 0 !important; }
        }
      `}</style>
    </div>
  );
};

export default VisitView;
